import * as yup from 'yup'
import { CSV_FILE_UPLOAD_CONFIG } from 'components/fileUploader/fileUploader'

export enum CloneModule {
  INVENTORY = 'INVENTORY',
  PRICING = 'PRICING',
}

export const validCloneFormDataSchema = yup.object().shape({
  module: yup
    .string()
    .oneOf(Object.values(CloneModule))
    .required('Module selection is required'),

  fromStore: yup.string().required('Select source store'),

  toStores: yup
    .array()
    .of(yup.string().required())
    .min(1, 'Select at least one destination store')
    .required('Select at least one destination store')
    .test(
      'store-validation',
      'Destination stores must be different from the source store',
      function (value) {
        const { module, fromStore } = this.parent

        if (module === CloneModule.PRICING) return true

        return !value?.includes(fromStore)
      },
    ),

  fromAggregator: yup.string().when('module', {
    is: CloneModule.PRICING,
    then: (s) => s.required('Select source aggregator'),
    otherwise: (s) => s.optional(),
  }),

  toAggregators: yup
    .array()
    .of(yup.string().required())
    .when('module', {
      is: CloneModule.PRICING,
      then: (s) =>
        s
          .min(1, 'Select at least one destination aggregator')
          .required('Select at least one destination aggregator'),
      otherwise: (s) => s.optional(),
    })
    .test(
      'pair-validation',
      'At least one destination store + aggregator combination matches the source',
      function (value) {
        const { module, fromStore, fromAggregator, toStores } = this.parent

        if (module !== CloneModule.PRICING) return true
        if (!value?.length || !toStores?.length) return true

        const hasIdenticalPair = (toStores as string[]).some(
          (toStore: string) =>
            toStore === fromStore && value.includes(fromAggregator),
        )

        return !hasIdenticalPair
      },
    ),

  copyAllBarcode: yup.boolean().required('Copy all barcode is required'),

  file: yup.mixed<File>().when('copyAllBarcode', {
    is: false,
    then: (schema) =>
      schema
        .required('Please upload a CSV file to proceed')
        .test(
          'is-file',
          'Invalid file selection',
          (value) => value instanceof File,
        )
        .test('fileType', 'Only CSV files are allowed', (file) => {
          if (!file) return false
          const validMimeTypes = CSV_FILE_UPLOAD_CONFIG.allowedMimeTypes
          const isCsvExt = file.name.toLowerCase().endsWith('.csv')
          return validMimeTypes.includes(file.type) && isCsvExt
        })
        .test(
          'fileSize',
          `File size exceeds ${CSV_FILE_UPLOAD_CONFIG.maxMB}MB limit`,
          (file) => {
            if (!file) return false
            return file.size <= CSV_FILE_UPLOAD_CONFIG.maxSize
          },
        ),
    otherwise: (schema) => schema.nullable().optional(),
  }),
})

export type CloneFormData = yup.InferType<typeof validCloneFormDataSchema>
